import { useState, useEffect, useMemo } from 'react';
import { useFormBuilderStore } from '../store/form-builder.store';
import { useMappingsStore } from '../store/mappings.store';
import { useCrmStore } from '../store/crm.store';
import { FormField, FieldType, PathHop } from '@/features/application/types/forms';
import { CrmField } from '../types';
import {
  isFormTypeCrmTypeCompatible,
  createFieldMappingUpdate,
  validateMaxLength,
  checkForDuplicateMapping,
  getCompatibleFormTypes,
  validateLeadingZeroRisk,
} from '../utils/field-mapping-validation.utils';
import { generateMappingSummary } from '../utils/mapping-summary.utils';

type ActiveStep = 'path' | 'field';

interface TypeChangeModalState {
  isOpen: boolean;
  compatibleTypes: FieldType[];
  selectedCrmField: CrmField | null;
}

interface DuplicateModalState {
  isOpen: boolean;
  conflictingFields: FormField[];
  crmFieldLabel: string;
}

interface LeadingZeroWarningModalState {
  isOpen: boolean;
  onConfirm: () => void;
  formFieldLabel: string;
  crmFieldLabel: string;
  crmFieldType: string;
}

interface MaxLengthModalState {
  isOpen: boolean;
  validationError: {
    formFieldMaxLength: number;
    crmFieldLength: number;
    fieldLabel: string;
    crmFieldLabel: string;
  } | null;
}

export const useCrmMapping = (
  field: FormField,
  onMappingComplete?: () => void,
  isActive?: boolean
) => {
  const { getCurrentPrimaryObject } = useMappingsStore();
  const { updateFieldInCurrentMapping } = useFormBuilderStore();
  const {
    getFieldsForObject,
    isLoadingFieldsForObject,
    fetchCrmObjectRelationships,
    getRelationshipsForObject: _getRelationshipsForObject, // Prefixed with underscore since unused
  } = useCrmStore();

  const primaryObject = getCurrentPrimaryObject();

  // Get target object from path
  const targetObjectType = useMemo(() => {
    if (!field.path || field.path.length === 0) return primaryObject;

    const lastHop = field.path[field.path.length - 1];
    return lastHop.object;
  }, [field.path, primaryObject]);

  const [activeStep, setActiveStep] = useState<ActiveStep | null>(() => {
    if (field.fieldApiName) return null; // Already complete, nothing to do.
    if (field.isPathConfirmed) return 'field'; // Path is confirmed, start at field.
    return 'path'; // Default start.
  });

  const [selectedFieldApiName, setSelectedFieldApiName] = useState(field.fieldApiName || '');
  const [isFieldComboboxOpen, setIsFieldComboboxOpen] = useState(false);

  // Modal state for type conversion confirmation
  const [typeChangeModal, setTypeChangeModal] = useState<TypeChangeModalState>({
    isOpen: false,
    compatibleTypes: [],
    selectedCrmField: null,
  });

  // Modal state for duplicate mapping warning
  const [duplicateModal, setDuplicateModal] = useState<DuplicateModalState>({
    isOpen: false,
    conflictingFields: [],
    crmFieldLabel: '',
  });

  const [leadingZeroWarningModal, setLeadingZeroWarningModal] =
    useState<LeadingZeroWarningModalState>({
      isOpen: false,
      onConfirm: () => {},
      formFieldLabel: '',
      crmFieldLabel: '',
      crmFieldType: '',
    });

  // Modal state for max length validation warning
  const [maxLengthModal, setMaxLengthModal] = useState<MaxLengthModalState>({
    isOpen: false,
    validationError: null,
  });

  // Modal state for missing locator when using child relationships
  const [locatorRequiredModalOpen, setLocatorRequiredModalOpen] = useState(false);

  const isFieldComplete = !!field.fieldApiName;

  // Mapping display logic
  const isMapped = !!field.fieldApiName;

  const getMappingSummary = () => {
    return generateMappingSummary(field);
  };

  // Fetch fields for the target object
  useEffect(() => {
    if (targetObjectType) {
      fetchCrmObjectRelationships(targetObjectType);
    }
  }, [targetObjectType, fetchCrmObjectRelationships]);

  // Synchronously initialize path with anchor hop if empty
  useEffect(() => {
    if (primaryObject && (!field.path || field.path.length === 0)) {
      const anchorHop: PathHop = {
        object: primaryObject,
        kind: 'anchor',
      };
      updateFieldInCurrentMapping(field.id, { path: [anchorHop] });
    }
  }, [primaryObject, field.path, field.id, updateFieldInCurrentMapping]);

  // Reset to appropriate step when item becomes active (unless already fully mapped)
  useEffect(() => {
    if (isActive && !field.fieldApiName) {
      // Only reset to path if the path hasn't been confirmed yet
      if (!field.isPathConfirmed) {
        setActiveStep('path');
      } else {
        setActiveStep('field');
      }
    }
  }, [isActive, field.fieldApiName, field.isPathConfirmed]);

  // Keep combobox selected value in sync with hydrated field mapping
  useEffect(() => {
    if (field.fieldApiName && selectedFieldApiName !== field.fieldApiName) {
      setSelectedFieldApiName(field.fieldApiName);
    }
    if (!field.fieldApiName && selectedFieldApiName) {
      setSelectedFieldApiName('');
    }
  }, [field.fieldApiName, selectedFieldApiName]);

  const availableFields = targetObjectType ? getFieldsForObject(targetObjectType) : [];
  const isLoading = targetObjectType ? isLoadingFieldsForObject(targetObjectType) : false;

  const handleConfirmStep = (step: ActiveStep) => {
    if (step === 'path') {
      // Explicitly mark path as confirmed in the field data
      updateFieldInCurrentMapping(field.id, { isPathConfirmed: true });
      setActiveStep('field');
      setIsFieldComboboxOpen(true);
    }
  };

  const handlePathChange = (newPath: PathHop[]) => {
    updateFieldInCurrentMapping(field.id, { path: newPath });
  };

  const validateAllLocators = (): boolean => {
    if (!field.path) return true;

    // Check if all child hops have locators
    for (const hop of field.path) {
      if (hop.kind === 'child' && !hop.locator) {
        setLocatorRequiredModalOpen(true);
        return false;
      }
    }

    return true;
  };

  const handleConfirmMapping = async () => {
    if (!selectedFieldApiName) return;

    const selectedField = availableFields?.find((f: CrmField) => f.id === selectedFieldApiName);
    if (!selectedField) return;

    // Validate all locators before proceeding
    const locatorsValid = validateAllLocators();
    if (!locatorsValid) return;

    const proceedWithMapping = () => {
      // Check for duplicate mapping
      const duplicateCheck = checkForDuplicateMapping(field.path, selectedFieldApiName, field.id);

      if (Array.isArray(duplicateCheck) && duplicateCheck.length > 0) {
        setDuplicateModal({
          isOpen: true,
          conflictingFields: duplicateCheck,
          crmFieldLabel: selectedField.label,
        });
        return;
      }

      // Check type compatibility
      if (!isFormTypeCrmTypeCompatible(field.type, selectedField.type)) {
        const compatibleTypes = getCompatibleFormTypes(selectedField.type);
        setTypeChangeModal({
          isOpen: true,
          compatibleTypes,
          selectedCrmField: selectedField,
        });
        return;
      }

      // Check max length compatibility
      if (field.formFieldMaxLength && selectedField.length) {
        const maxLengthCheck = validateMaxLength(field, selectedField);
        if (maxLengthCheck) {
          setMaxLengthModal({
            isOpen: true,
            validationError: {
              formFieldMaxLength: maxLengthCheck.formFieldMaxLength,
              crmFieldLength: maxLengthCheck.crmFieldLength,
              fieldLabel: field.label,
              crmFieldLabel: selectedField.label,
            },
          });
          return;
        }
      }

      // All validations passed, apply the mapping
      applyCrmMapping(selectedField, field.type);
    };

    // New: Check for leading zero truncation risk
    if (validateLeadingZeroRisk(field, selectedField.type)) {
      setLeadingZeroWarningModal({
        isOpen: true,
        onConfirm: () => {
          handleLeadingZeroWarningClose();
          proceedWithMapping(); // User chose to ignore risk
        },
        formFieldLabel: field.label,
        crmFieldLabel: selectedField.label,
        crmFieldType: selectedField.type,
      });
    } else {
      proceedWithMapping(); // No risk, proceed directly
    }
  };

  const applyCrmMapping = (selectedField: CrmField, newFieldType: FieldType) => {
    // Fix: provide field as first argument
    const mappingUpdate = createFieldMappingUpdate(field, selectedField, newFieldType);

    updateFieldInCurrentMapping(field.id, {
      ...mappingUpdate,
      fieldApiName: selectedFieldApiName,
      fieldLabel: selectedField.label,
      isPathConfirmed: true, // Ensure path is marked as confirmed when mapping is complete
    });

    finalizeMappingUI();
  };

  const finalizeMappingUI = () => {
    setActiveStep(null);
    onMappingComplete?.();
  };

  const handleTypeChangeModalClose = () => {
    setTypeChangeModal({
      isOpen: false,
      compatibleTypes: [],
      selectedCrmField: null,
    });
  };

  const handleTypeChangeModalConfirm = (newType: FieldType) => {
    if (typeChangeModal.selectedCrmField) {
      applyCrmMapping(typeChangeModal.selectedCrmField, newType);
    }
    handleTypeChangeModalClose();
  };

  const handleDuplicateModalClose = () => {
    setDuplicateModal({
      isOpen: false,
      conflictingFields: [],
      crmFieldLabel: '',
    });
  };

  const handleMaxLengthModalClose = () => {
    setMaxLengthModal({
      isOpen: false,
      validationError: null,
    });
  };

  const handleLeadingZeroWarningClose = () => {
    setLeadingZeroWarningModal({
      isOpen: false,
      onConfirm: () => {},
      formFieldLabel: '',
      crmFieldLabel: '',
      crmFieldType: '',
    });
  };

  return {
    // State
    activeStep,
    setActiveStep,
    selectedFieldApiName,
    setSelectedFieldApiName,
    availableFields,
    isLoading,
    isFieldComplete,

    // Combobox state
    isFieldComboboxOpen,
    setIsFieldComboboxOpen,

    // Mapping display
    isMapped,
    getMappingSummary,

    // Modal states
    typeChangeModal,
    duplicateModal,
    maxLengthModal,
    locatorRequiredModalOpen,
    setLocatorRequiredModalOpen,
    leadingZeroWarningModal,

    // Handlers
    handleConfirmStep,
    handlePathChange,
    handleConfirmMapping,
    handleTypeChangeModalClose,
    handleTypeChangeModalConfirm,
    handleDuplicateModalClose,
    handleMaxLengthModalClose,
    handleLeadingZeroWarningClose,
  };
};
